import React, { useState } from "react";
import { useLanguage } from "../../../../context/LanguageContext";
import { uploadFile, DEFAULT_STORE_ID } from "../../../../firebase/firebase";

const PaymentMethodSection = ({
  formData,
  setFormData,
  storeSettings,
  processing,
}) => {
  const { language } = useLanguage();
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptError, setReceiptError] = useState("");

  const isFormCompleted = React.useCallback(() => {
    // Check if shipping is required for this store
    const requiresShipping = storeSettings?.requiresShipping !== false;

    const billing = formData.billingInfo;
    const shipping = formData.sameAsBilling ? billing : formData.shippingInfo;

    // Basic personal info always required
    const isPersonalInfoComplete =
      billing.firstName && billing.lastName && billing.email && billing.phone;

    // If shipping not required, only check personal info
    if (!requiresShipping) {
      return isPersonalInfoComplete;
    }

    // If shipping is required, check address and shipping method
    const isBillingComplete =
      isPersonalInfoComplete &&
      billing.address &&
      billing.city &&
      billing.country;

    const isShippingComplete =
      formData.sameAsBilling ||
      (shipping.firstName &&
        shipping.lastName &&
        shipping.address &&
        shipping.city &&
        shipping.country);

    const isShippingMethodSelected = !!formData.shippingMethod;

    return isBillingComplete && isShippingComplete && isShippingMethodSelected;
  }, [formData, storeSettings]);

  React.useEffect(() => {
    if (!isFormCompleted() && formData.paymentMethod) {
      setFormData((prev) => ({ ...prev, paymentMethod: "" }));
    }
  }, [
    formData.billingInfo,
    formData.shippingInfo,
    formData.sameAsBilling,
    formData.shippingMethod,
    formData.paymentMethod,
    isFormCompleted,
    setFormData,
  ]);

  const translations = {
    en: {
      paymentMethod: "Payment Method",
      cashOnDelivery: "Cash on Delivery",
      creditCard: "Pay Online",
      creditCardSubtext: "Secure payment via Stripe",
      directBankTransfer: "Bank Transfer",
      directBankTransferSubtext: "Pay via bank transfer and upload receipt",
      bankDetails: "Bank Transfer Details",
      uploadReceipt: "Upload Payment Receipt",
      receiptRequired: "Receipt is required",
      uploading: "Uploading...",
      receiptUploaded: "Receipt uploaded successfully",
    },
    ar: {
      paymentMethod: "طريقة الدفع",
      cashOnDelivery: "الدفع عند الاستلام",
      creditCard: "بطاقة ائتمان/خصم",
      creditCardSubtext: "دفع آمن عبر Stripe",
      directBankTransfer: "التحويل البنكي",
      directBankTransferSubtext: "الدفع عن طريق التحويل البنكي ورفع الإيصال",
      bankDetails: "تفاصيل التحويل البنكي",
      uploadReceipt: "رفع إيصال الدفع",
      receiptRequired: "الإيصال مطلوب",
      uploading: "جاري الرفع...",
      receiptUploaded: "تم رفع الإيصال بنجاح",
    },
  };

  const t = translations[language] || translations.en;

  const handlePaymentMethodChange = (method) => {
    setFormData((prev) => ({
      ...prev,
      paymentMethod: method,
      paymentReceipt: method === "manual_transfer" ? prev.paymentReceipt : null,
    }));
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      setReceiptError("Please upload a valid image (JPG, PNG) or PDF file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setReceiptError("File size must be less than 5MB");
      return;
    }

    setUploadingReceipt(true);
    setReceiptError("");

    try {
      const receiptUrl = await uploadFile(
        DEFAULT_STORE_ID,
        `payment-receipts/${Date.now()}_${file.name}`,
        file,
      );
      setFormData((prev) => ({
        ...prev,
        paymentReceipt: receiptUrl,
      }));
    } catch (error) {
      console.error("Error uploading receipt:", error);
      setReceiptError("Failed to upload receipt. Please try again.");
    } finally {
      setUploadingReceipt(false);
    }
  };

  const formCompleted = isFormCompleted();

  if (!formCompleted) {
    return null;
  }

  return (
    <div className="checkout-section">
      <h2>{t.paymentMethod}</h2>

      <div className="payment-methods">
        {storeSettings?.paymentMethods?.cashOnDelivery === true && (
          <div className="payment-method">
            <input
              type="radio"
              id="cash-on-delivery"
              name="payment-method"
              value="cash_on_delivery"
              checked={formData.paymentMethod === "cash_on_delivery"}
              onChange={() => handlePaymentMethodChange("cash_on_delivery")}
              disabled={processing}
            />
            <label htmlFor="cash-on-delivery" className="payment-method-label">
              <div className="payment-method-main">
                <span className="payment-title">{t.cashOnDelivery}</span>
              </div>
            </label>
          </div>
        )}

        {storeSettings?.paymentMethods?.creditCard === true && (
          <>
            {/* <div className="payment-method">
              <input
                type="radio"
                id="credit-card"
                name="payment-method"
                value="credit_card"
                checked={formData.paymentMethod === "credit_card"}
                onChange={() => handlePaymentMethodChange("credit_card")}
                disabled={processing}
              />
              <label htmlFor="credit-card" className="payment-method-label">
                <div className="payment-method-main">
                  <div className="payment-details">
                    <span className="payment-title">{t.creditCard}</span>
                    <span className="payment-subtext">
                      {t.creditCardSubtext}
                    </span>
                  </div>
                </div>
                <div className="stripe-badges">
                  <span className="stripe-badge">Visa</span>
                  <span className="stripe-badge">Mastercard</span>
                </div>
              </label>
            </div> */}

            <div className="payment-method">
              <input
                type="radio"
                id="hosted-checkout"
                name="payment-method"
                value="hosted_checkout"
                checked={formData.paymentMethod === "hosted_checkout"}
                onChange={() => handlePaymentMethodChange("hosted_checkout")}
                disabled={processing}
              />
              <label htmlFor="hosted-checkout" className="payment-method-label">
                <div className="payment-method-main">
                  <div className="payment-details">
                    <span className="payment-title">
                      {t.hostedCheckout || "Pay with Apple Pay / Google Pay"}
                    </span>
                    <span className="payment-subtext">
                      {t.hostedCheckoutSubtext ||
                        "Secure checkout with more payment options"}
                    </span>
                  </div>
                </div>
                <div className="stripe-badges">
                  <span className="stripe-badge">Apple Pay</span>
                  <span className="stripe-badge">Google Pay</span>
                  <span className="stripe-badge">Link</span>
                </div>
              </label>
            </div>
          </>
        )}

        {storeSettings?.paymentMethods?.directBankTransfer === true && (
          <div className="payment-method">
            <input
              type="radio"
              id="manual-transfer"
              name="payment-method"
              value="manual_transfer"
              checked={formData.paymentMethod === "manual_transfer"}
              onChange={() => handlePaymentMethodChange("manual_transfer")}
              disabled={processing}
            />
            <label htmlFor="manual-transfer" className="payment-method-label">
              <div className="payment-method-main">
                <div className="payment-details">
                  <span className="payment-title">{t.directBankTransfer}</span>
                  <span className="payment-subtext">
                    {t.directBankTransferSubtext}
                  </span>
                </div>
              </div>
            </label>
          </div>
        )}

        {formData.paymentMethod === "manual_transfer" && (
          <div className="bank-transfer-info">
            <h4>{t.bankDetails}</h4>
            <div className="bank-details-text">
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  margin: 0,
                }}
              >
                {storeSettings?.directBankTransferDetails?.[language] ||
                  storeSettings?.directBankTransferDetails?.en ||
                  "Bank details not configured"}
              </pre>
            </div>

            <div className="receipt-upload-section">
              <label htmlFor="receipt-upload" className="receipt-upload-label">
                {t.uploadReceipt} <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="file"
                id="receipt-upload"
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                onChange={handleReceiptUpload}
                disabled={uploadingReceipt || processing}
                style={{ marginTop: "8px" }}
              />
              {uploadingReceipt && (
                <p className="upload-status">{t.uploading}</p>
              )}
              {formData.paymentReceipt && !uploadingReceipt && (
                <p className="upload-status success">{t.receiptUploaded}</p>
              )}
              {receiptError && <p className="error-message">{receiptError}</p>}
              {!formData.paymentReceipt && !uploadingReceipt && (
                <p className="help-text">{t.receiptRequired}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodSection;
